'use client'

import { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'
import SafeHTML from './safe-html'

import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
interface TruncatedHTMLProps {
  html: string
  maxLines?: number
  className?: string
  showMoreText?: string
  showLessText?: string
  textClassName?: string
  buttonClassName?: string
}

export function TruncatedHTML({
  html,
  maxLines = 2,
  className,
  showMoreText,
  showLessText,
  textClassName,
  buttonClassName,
}: TruncatedHTMLProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current
      if (!element) return

      const lineHeight = parseInt(window.getComputedStyle(element).lineHeight)
      const maxHeight = lineHeight * maxLines
      setIsTruncated(element.scrollHeight > maxHeight)
    }

    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [maxLines, html])

  return (
    <div className={clsx('relative', className)}>
      <div
        ref={textRef}
        className={clsx(
          'text-sm leading-relaxed transition-all duration-200',
          !isExpanded && `line-clamp-${maxLines}`,
          textClassName
        )}
      >
        <SafeHTML html={html} />
      </div>

      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={clsx(
            'font-lexend text-[12px] font-medium text-primary-500 hover:text-primary-500',
            buttonClassName
          )}
        >
          {isExpanded
            ? (showLessText ?? t('booking.showLess'))
            : (showMoreText ?? t('booking.showMore'))}
        </button>
      )}
    </div>
  )
}
